// Content Security Policy — Script inventory.
//
// PCI DSS 4.0.1 6.4.3 asks operators to inventory every script
// that runs on payment pages. This page renders the CH-backed
// dedup + classification shipped in Phase 3.1a as a filterable
// table + a Download CSV button — enough to hand a QSA the raw
// evidence today, before Phase 3.2 layers authorisation /
// justification tracking on top.
//
// Palette + primitives borrowed from Config.js so the two CSP
// pages read as one visual family.

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Stack,
  Typography,
  Box,
  CircularProgress,
  Chip,
  Checkbox,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  Radio,
  FormControlLabel,
  Divider,
  Select,
  MenuItem,
} from "@mui/material";

import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import PolicyOutlinedIcon from "@mui/icons-material/PolicyOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import FingerprintOutlinedIcon from "@mui/icons-material/FingerprintOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import CachedOutlinedIcon from "@mui/icons-material/CachedOutlined";
import RadioButtonUncheckedOutlinedIcon from "@mui/icons-material/RadioButtonUncheckedOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { FileCode2, Globe, Home, Code2 } from "lucide-react";

import { useNotification } from "../../../../../contexts/NotificationContext";
import V2PageHeader from "../../../../common/V2PageHeader";
import SiteScopeGate from "../../../../common/SiteScopeGate";
import RefreshIconButton from "../../../../common/RefreshIconButton";
import colors from "../../../../../utils/colors";

import useCspConfig from "../../../../../hooks/user/useCspConfig";
import useGlobalSite from "../../../../../hooks/user/useGlobalSite";
import { AsecEventsFilterBar } from "../api_security/components/AsecEventsFilterBar";
import { filterSelectSx } from "../api_security/asecUi";
import { GhostButton, primaryButtonSx, cancelButtonSx } from "../../../../common/Button";

// ── palette ─────────────────────────────────────────────────
const INDIGO = colors.primary[300];
const INDIGO_BG = "rgba(99, 113, 247, 0.08)";
const BORDER = colors.neutral[200];
const FG1 = colors.neutral[1000];
const FG2 = colors.neutral[700];
const FG3 = colors.neutral[500];
const NEUTRAL_BG = colors.neutral[100];
const SUCCESS_BG = "#E8FBEF";
const SUCCESS_FG = "#0f7a42";
const SUCCESS_BORDER = "#C8E6D4";
const AMBER_BG = "#FFF6D0";
const AMBER_FG = "#7a5b10";
const AMBER_BORDER = "#F2DE9A";
const DANGER_BG = "#FEECEE";
const DANGER_FG = "#A11228";
const DANGER_BORDER = "#F4C7CD";

const CLASSIFICATION_META = {
  first_party: { label: "First party", bg: SUCCESS_BG, fg: SUCCESS_FG, br: SUCCESS_BORDER },
  third_party: { label: "Third party", bg: INDIGO_BG, fg: INDIGO, br: "rgba(99,113,247,0.28)" },
  inline: { label: "Inline", bg: AMBER_BG, fg: AMBER_FG, br: AMBER_BORDER },
  extension: { label: "Browser extension", bg: NEUTRAL_BG, fg: FG2, br: BORDER },
  unknown: { label: "Unknown", bg: DANGER_BG, fg: DANGER_FG, br: DANGER_BORDER },
};

// Phase 3.2 — PCI DSS 6.4.3 authorization ledger visual tokens.
const STATUS_META = {
  pending: { label: "Pending review", bg: AMBER_BG, fg: AMBER_FG, br: AMBER_BORDER },
  authorized: { label: "Authorized", bg: SUCCESS_BG, fg: SUCCESS_FG, br: SUCCESS_BORDER },
  unauthorized: { label: "Unauthorized", bg: DANGER_BG, fg: DANGER_FG, br: DANGER_BORDER },
};

// Phase 3.3 — PCI DSS 11.6.1 integrity ledger visual tokens.
const INTEGRITY_META = {
  ok: { label: "OK", bg: SUCCESS_BG, fg: SUCCESS_FG, br: SUCCESS_BORDER, Icon: CheckCircleOutlineOutlinedIcon },
  changed: { label: "Changed", bg: DANGER_BG, fg: DANGER_FG, br: DANGER_BORDER, Icon: ErrorOutlineOutlinedIcon },
  unreachable: { label: "Unreachable", bg: AMBER_BG, fg: AMBER_FG, br: AMBER_BORDER, Icon: CachedOutlinedIcon },
  error: { label: "Fetch error", bg: AMBER_BG, fg: AMBER_FG, br: AMBER_BORDER, Icon: ErrorOutlineOutlinedIcon },
  unchecked: { label: "Not checked", bg: NEUTRAL_BG, fg: FG3, br: BORDER, Icon: RadioButtonUncheckedOutlinedIcon },
};

const wafLikeSelectSx = {
  minWidth: 220,
  "& .MuiOutlinedInput-root": {
    height: 36,
    borderRadius: "8px",
    backgroundColor: "#fff",
    "& fieldset": { borderColor: BORDER },
    "&:hover fieldset": { borderColor: INDIGO },
    "&.Mui-focused fieldset": { borderColor: INDIGO },
  },
  "& .MuiSelect-select": {
    fontSize: 14,
    color: FG2,
    display: "flex",
    alignItems: "center",
  },
};

// Phase 4b follow-up — per-row SRI readiness. Mirrors pingora's
// loader gate: a row is SRI-injected only when authorized AND
// carries a recorded contentSha256. Site-level `sri_mode=enforce`
// is a separate axis, communicated by the site-wide banner.
const SRI_ROW_META = {
  ready: {
    label: "Ready",
    tip: "This row has a recorded SHA-256. Pingora will splice `integrity=` on matching tags when SRI is enforcing.",
    bg: SUCCESS_BG, fg: SUCCESS_FG, br: SUCCESS_BORDER,
  },
  pending_hash: {
    label: "Pending hash",
    tip: "Authorized but no reference hash yet. Run \"Check integrity\" or wait for the daily watcher — until then this row is untouched by the SRI rewriter.",
    bg: AMBER_BG, fg: AMBER_FG, br: AMBER_BORDER,
  },
  not_authorized: {
    label: "—",
    tip: "Pending review or unauthorized. Only authorized scripts are eligible for SRI enforcement.",
    bg: NEUTRAL_BG, fg: FG3, br: BORDER,
  },
  not_fetchable: {
    label: "—",
    tip: "Inline / extension / non-http(s) classification is not fetchable, so no hash and no SRI.",
    bg: NEUTRAL_BG, fg: FG3, br: BORDER,
  },
};

function sriRowState(row) {
  if (row.classification === "inline" || row.classification === "extension") {
    return "not_fetchable";
  }
  if ((row.status || "pending") !== "authorized") {
    return "not_authorized";
  }
  return row.content_sha256 ? "ready" : "pending_hash";
}

const WINDOW_OPTIONS = [
  { hours: 24, label: "24h" },
  { hours: 168, label: "7d" },
  { hours: 24 * 30, label: "30d" },
];

const EXPORT_OPTIONS = [
  { value: "csv", label: "Download CSV" },
  { value: "pci-csv", label: "PCI evidence CSV" },
  { value: "pci-pdf", label: "PCI evidence PDF" },
];

function fmtNumber(n) {
  if (n == null) return "—";
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  return num.toLocaleString();
}

function fmtWhen(ts) {
  if (!ts) return "—";
  const d = new Date(Number(ts));
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function shortenUri(u, max = 60) {
  if (!u) return "—";
  if (u.length <= max) return u;
  return u.slice(0, max - 1) + "…";
}

// ── SectionCard ─────────────────────────────────────────────
function SectionCard({ icon, title, desc, right, children }) {
  return (
    <Box
      sx={{
        background: "#fff",
        border: `1px solid ${BORDER}`,
        borderRadius: "14px",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          padding: "18px 20px 14px",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1.75,
          borderBottom: `1px solid ${BORDER}`,
        }}
      >
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", minWidth: 0 }}>
          {icon && (
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "9px",
                background: INDIGO_BG,
                color: INDIGO,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                "& svg": { width: 18, height: 18 },
              }}
            >
              {icon}
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, fontSize: 16, color: FG1, mb: 0.25 }}>
              {title}
            </Typography>
            {desc && (
              <Typography sx={{ fontSize: 12.5, color: FG2, lineHeight: 1.5, maxWidth: 640 }}>
                {desc}
              </Typography>
            )}
          </Box>
        </Box>
        {right}
      </Box>
      {children !== undefined && <Box sx={{ padding: "18px 20px" }}>{children}</Box>}
    </Box>
  );
}

function StatusTile({ Icon, label, value, sub }) {
  return (
    <Box
      sx={{
        padding: "14px 16px",
        border: `1px solid ${BORDER}`,
        borderRadius: "12px",
        background: "#fff",
        flex: 1,
        minWidth: 0,
      }}
    >
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: "6px",
            background: NEUTRAL_BG,
            color: FG2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "& svg": { width: 14, height: 14 },
          }}
        >
          <Icon />
        </Box>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: FG2, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          {label}
        </Typography>
      </Stack>
      <Typography sx={{ fontSize: 22, fontWeight: 700, color: FG1, lineHeight: 1.2 }}>
        {value}
      </Typography>
      {sub && (
        <Typography sx={{ fontSize: 12, color: FG3, mt: 0.5 }}>{sub}</Typography>
      )}
    </Box>
  );
}

function ScriptInventory() {
  const { effectiveSiteUid } = useGlobalSite();
  const { showSuccess, showError } = useNotification();
  const {
    getScriptInventory,
    authorizeScript,
    recheckIntegrity,
    acknowledgeDrift,
    getCspConfig,
    cspConfig,
    downloadScriptInventoryCsv,
    downloadPciEvidenceCsv,
    downloadPciEvidencePdf,
    getSharedIntel,
  } = useCspConfig();

  const site_uid = effectiveSiteUid || null;

  const [windowHours, setWindowHours] = useState(24 * 30);
  const [inventory, setInventory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  // Lazy initializer reads window.location directly so the deep-link
  // ?highlightUrl=<encoded> from Config.js's violation drawer lands
  // *before* first paint — no post-mount useEffect race against
  // React Router's location commit.
  const [query, setQuery] = useState(() => {
    if (typeof window === "undefined") return "";
    try {
      const p = new URLSearchParams(window.location.search);
      return p.get("highlightUrl") || "";
    } catch { return ""; }
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  // Phase 3.2 — the row currently being reviewed. The dialog owns
  // the draft (status + justification); we upsert on Save and
  // refresh the inventory afterwards.
  const [reviewRow, setReviewRow] = useState(null);
  // Phase 3.3 — spinner state for the manual PCI 11.6.1 re-check.
  const [rechecking, setRechecking] = useState(false);
  // Gap-closure sprint — bulk-select for multi-row Authorize / Mark
  // unauthorized. Selection is a Set<url> so we don't have to
  // reconcile with the loaded row objects on every render, and it
  // survives pagination + filter changes.
  const [selectedUrls, setSelectedUrls] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // Gap-closure sprint — cross-customer intel v0. Map of urlHash →
  // count of OTHER Layerd sites that also saw this blocked URI in
  // the last 24 h. Shown as a small chip on inventory rows so the
  // operator knows a suspicious script isn't unique to their site.
  const [sharedSignatures, setSharedSignatures] = useState({});
  // Deep-link support — if the operator lands on this page from the
  // Violations table (?highlightUrl=<encoded>), pre-fill the search
  // box + scroll the row into view once the inventory has loaded.
  const location = useLocation();
  const navigate = useNavigate();
  // Same lazy-init pattern as `query` so the row highlight lands
  // before first paint.
  const [highlightUrl, setHighlightUrl] = useState(() => {
    if (typeof window === "undefined") return null;
    try {
      const p = new URLSearchParams(window.location.search);
      return p.get("highlightUrl") || null;
    } catch { return null; }
  });
  useEffect(() => {
    // Strip the ?highlightUrl= param once we've consumed it, so a
    // page-refresh doesn't re-trigger the highlight after the
    // operator has already scrolled past it. Replace, not push, to
    // keep the back button useful. Uses location (not window) so we
    // preserve any other params React Router owns.
    if (highlightUrl && location.search.includes("highlightUrl=")) {
      navigate(location.pathname, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = useCallback(async () => {
    if (!site_uid) return;
    setLoading(true);
    try {
      const r = await getScriptInventory(site_uid, {
        windowHours,
        page,
        limit: rowsPerPage,
        status: statusFilter,
        classification: filter,
        q: query,
      });
      setInventory(r);
    } catch (e) {
      showError(
        e?.response?.data?.message || e?.message || "Failed to load script inventory."
      );
    } finally {
      setLoading(false);
    }
  }, [site_uid, windowHours, getScriptInventory, showError, page, rowsPerPage, statusFilter, filter, query]);

  useEffect(() => {
    run();
  }, [run]);

  useEffect(() => { setPage(0); }, [windowHours, statusFilter, filter, query, site_uid]);

  // Phase 4c — fetch cspConfig alongside inventory so we know the
  // site's sri_mode and can render the coverage banner + per-row
  // "will be enforced" indicator. Non-fatal on failure (banner
  // falls back to a neutral state).
  useEffect(() => {
    if (site_uid) getCspConfig(site_uid);
  }, [site_uid, getCspConfig]);

  // Cross-tenant reach lookup — fires once per inventory load.
  // We batch the loaded URLs (max 200) into a single POST to the
  // shared-intel endpoint and merge the response into
  // sharedSignatures. Fail-open: if the endpoint is unreachable, we
  // just don't render the chips; the rest of the page stays
  // functional.
  useEffect(() => {
    const urls = (inventory?.scripts || [])
      .map((s) => s.url)
      .filter((u) => typeof u === "string" && u.length > 0)
      .slice(0, 200);
    if (!site_uid || urls.length === 0 || !getSharedIntel) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await getSharedIntel(site_uid, urls);
        if (!cancelled && r?.urls) setSharedSignatures(r.urls);
      } catch (e) {
        // silent — feature is enrichment-only
      }
    })();
    return () => { cancelled = true; };
  }, [site_uid, inventory, getSharedIntel]);

  const filtered = inventory?.scripts || [];
  const scriptsTotal = typeof inventory?.scripts_total === "number"
    ? inventory.scripts_total
    : filtered.length;
  const paged = filtered;

  // Fetch through the authenticated axios client (Bearer token
  // interceptor lives there) and then trigger a client-side blob
  // download. A plain `<a href>` click is a browser navigation and
  // skips axios interceptors — that path returns 401 "No bearer
  // token found" from the CP.
  const triggerBlobDownload = useCallback((blob, filename) => {
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Revoke on the next tick so Safari has time to actually kick
    // off the download before we invalidate the URL.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 500);
  }, []);

  const handleDownloadCsv = useCallback(async () => {
    if (!site_uid) return;
    try {
      const blob = await downloadScriptInventoryCsv(site_uid, { windowHours });
      const filename = `csp-script-inventory-${site_uid}-${windowHours}h.csv`;
      triggerBlobDownload(blob, filename);
    } catch (e) {
      showError(
        e?.response?.data?.message || e?.message || "Failed to download inventory CSV."
      );
    }
  }, [site_uid, windowHours, downloadScriptInventoryCsv, triggerBlobDownload, showError]);

  const handleCopyUrl = useCallback(
    (url) => {
      if (!url) return;
      try {
        navigator.clipboard.writeText(url);
        showSuccess("Script URL copied.");
      } catch (e) {
        showError("Copy failed — select the cell and press ⌘C.");
      }
    },
    [showSuccess, showError]
  );

  // Phase 3.2 — PCI DSS 6.4.3 evidence bundle download. Assessor-
  // facing artifact combining the inventory with the authorization
  // ledger.
  const handleDownloadPciEvidence = useCallback(async () => {
    if (!site_uid) return;
    try {
      const blob = await downloadPciEvidenceCsv(site_uid, { windowHours });
      const filename = `csp-pci-evidence-${site_uid}-${windowHours}h.csv`;
      triggerBlobDownload(blob, filename);
    } catch (e) {
      showError(
        e?.response?.data?.message || e?.message || "Failed to download PCI evidence."
      );
    }
  }, [site_uid, windowHours, downloadPciEvidenceCsv, triggerBlobDownload, showError]);

  // Branded QSA-facing PDF variant. Same underlying evidence bundle,
  // scoped to authorized (payment-page) scripts. Rendered server-side
  // via PDFKit + Inter + Layerd mark.
  const handleDownloadPciEvidencePdf = useCallback(async () => {
    if (!site_uid) return;
    try {
      const blob = await downloadPciEvidencePdf(site_uid, { windowHours });
      const day = new Date().toISOString().slice(0, 10);
      const filename = `csp-pci-evidence-${site_uid}-${day}.pdf`;
      triggerBlobDownload(blob, filename);
    } catch (e) {
      showError(
        e?.response?.data?.message || e?.message || "Failed to download PCI evidence PDF."
      );
    }
  }, [site_uid, windowHours, downloadPciEvidencePdf, triggerBlobDownload, showError]);

  const handleExportChange = useCallback(
    (e) => {
      const v = e.target.value;
      if (v === "csv") handleDownloadCsv();
      else if (v === "pci-csv") handleDownloadPciEvidence();
      else if (v === "pci-pdf") handleDownloadPciEvidencePdf();
    },
    [handleDownloadCsv, handleDownloadPciEvidence, handleDownloadPciEvidencePdf]
  );

  const handleSaveReview = useCallback(
    async (draft) => {
      if (!site_uid || !reviewRow) return;
      try {
        await authorizeScript(site_uid, {
          url: reviewRow.url,
          classification: reviewRow.classification,
          host: reviewRow.host || "",
          scheme: reviewRow.scheme || "",
          status: draft.status,
          justification: draft.justification,
        });
        showSuccess("Authorization saved.");
        setReviewRow(null);
        await run();
      } catch (e) {
        showError(
          e?.response?.data?.message || e?.message || "Failed to save authorization."
        );
      }
    },
    [site_uid, reviewRow, authorizeScript, showSuccess, showError, run]
  );

  // Gap-closure sprint — bulk authorize / mark-unauthorized. The
  // backend authorize endpoint (Phase 3.2a) already accepts a
  // `{ items: [...] }` bulk shape so this is just a UI wrapper.
  // We fan the pending set out to items {url, classification,
  // status, justification} and let the server upsert atomically.
  const handleBulkAuthorize = useCallback(
    async (status) => {
      if (!site_uid || selectedUrls.size === 0) return;
      const rows = (inventory?.scripts || []).filter((s) => selectedUrls.has(s.url));
      if (rows.length === 0) return;
      const items = rows.map((s) => ({
        url: s.url,
        classification: s.classification,
        host: s.host || "",
        scheme: s.scheme || "",
        status,
        justification: "",
      }));
      setBulkBusy(true);
      try {
        await authorizeScript(site_uid, { items });
        showSuccess(
          `${items.length} script${items.length === 1 ? "" : "s"} marked ${status.replace("_", " ")}.`
        );
        setSelectedUrls(new Set());
        await run();
      } catch (e) {
        showError(
          e?.response?.data?.message || e?.message || "Bulk authorize failed."
        );
      } finally {
        setBulkBusy(false);
      }
    },
    [site_uid, selectedUrls, inventory, authorizeScript, showSuccess, showError, run]
  );

  const handleRecheckIntegrity = useCallback(async () => {
    if (!site_uid) return;
    setRechecking(true);
    try {
      const r = await recheckIntegrity(site_uid);
      const drift = Number(r?.changed || 0);
      if (drift > 0) {
        showError(`Integrity check found ${drift} drifted script${drift === 1 ? "" : "s"}. Review below.`);
      } else {
        showSuccess(`Integrity check complete: ${r?.checked || 0} scripts hashed, ${r?.ok || 0} unchanged.`);
      }
      await run();
    } catch (e) {
      showError(
        e?.response?.data?.message || e?.message || "Failed to run integrity check."
      );
    } finally {
      setRechecking(false);
    }
  }, [site_uid, recheckIntegrity, showSuccess, showError, run]);

  const handleAcknowledgeDrift = useCallback(
    async (row) => {
      if (!site_uid || !row?.url_hash) return;
      try {
        await acknowledgeDrift(site_uid, { urlHash: row.url_hash });
        showSuccess("Drift acknowledged — reference hash rotated.");
        setReviewRow(null);
        await run();
      } catch (e) {
        showError(
          e?.response?.data?.message || e?.message || "Failed to acknowledge drift."
        );
      }
    },
    [site_uid, acknowledgeDrift, showSuccess, showError, run]
  );

  const counts = inventory?.counts_by_classification || {};
  const statusCounts = inventory?.counts_by_status || {};
  const integrityCounts = inventory?.counts_by_integrity || {};
  const totalHits = inventory?.total_scripts_observed || 0;
  const uniqueCount = inventory?.unique_scripts || 0;
  const driftedCount = Number(integrityCounts.changed || 0);

  // Phase 4c — SRI coverage. Eligible = authorized rows with a
  // recorded content hash; those are the ones pingora will actually
  // splice `integrity=` onto. Authorized rows lacking a hash are
  // "pending" for SRI until the integrity daemon runs on them.
  const sriMode = cspConfig?.sri_mode || "off";
  const authorizedCount = Number(statusCounts.authorized || 0);
  const sriEligibleCount = useMemo(() => {
    if (!inventory?.scripts) return 0;
    return inventory.scripts.filter(
      (s) => (s.status || "pending") === "authorized" && s.content_sha256
    ).length;
  }, [inventory]);

  const statusFilterOptions = [
    { key: "all", label: "All statuses", count: uniqueCount },
    { key: "pending", label: "Pending review", count: statusCounts.pending || 0 },
    { key: "authorized", label: "Authorized", count: statusCounts.authorized || 0 },
    { key: "unauthorized", label: "Unauthorized", count: statusCounts.unauthorized || 0 },
  ];

  const filterOptions = [
    { key: "all", label: "All Classifications", count: uniqueCount },
    { key: "third_party", label: "Third party", count: counts.third_party || 0 },
    { key: "first_party", label: "First party", count: counts.first_party || 0 },
    { key: "inline", label: "Inline", count: counts.inline || 0 },
    { key: "extension", label: "Extension", count: counts.extension || 0 },
    { key: "unknown", label: "Unknown", count: counts.unknown || 0 },
  ];

  return (
    <SiteScopeGate mode="single">
      {() => (
        <>
          <Helmet>
            <title>CSP Script Inventory</title>
          </Helmet>
          <Stack spacing={3} sx={{ p: 4 }}>
            <V2PageHeader
              title="Script inventory"
              sx={{ pb: 1.5 }}
              descriptionSx={{ maxWidth: 760, mt: 1 }}
              description="Every script the browser has been observed loading on this site, derived from the CSP violation history. Feeds PCI DSS 6.4.3 payment-page script inventory + evidence."
              actions={(
                <Stack direction="row" spacing={1} alignItems="center">
                  <Select
                    value=""
                    displayEmpty
                    onChange={handleExportChange}
                    renderValue={() => "Export"}
                    sx={{ ...filterSelectSx, minWidth: 130 }}
                  >
                    {EXPORT_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <RefreshIconButton
                    onClick={run}
                    disabled={loading}
                    loading={loading}
                    size="compact"
                  />
                </Stack>
              )}
            />

            {!inventory && loading && (
              <Stack alignItems="center" sx={{ mt: 2 }}>
                <CircularProgress size={28} sx={{ color: INDIGO }} />
              </Stack>
            )}

            {inventory && (
              <Stack spacing={2.5}>
              {/* Headline stats */}
              <SectionCard
                icon={<InventoryOutlinedIcon />}
                title="Observed inventory"
                desc="Deduplicated across script-src / script-src-elem / script-src-attr violations, grouped by URL."
                right={
                  <Tooltip title="Re-hash every authorized script (PCI DSS 11.6.1 check)">
                    <span>
                      <GhostButton
                        onClick={handleRecheckIntegrity}
                        disabled={rechecking}
                        size="small"
                        startIcon={rechecking ? <CircularProgress size={14} sx={{ color: FG2 }} /> : <FingerprintOutlinedIcon />}
                        sx={{
                          minWidth: 170,
                          px: 2,
                        }}
                      >
                        {rechecking ? "Checking…" : "Check integrity"}
                      </GhostButton>
                    </span>
                  </Tooltip>
                }
              >
                <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                  <StatusTile
                    Icon={FileCode2}
                    label="Unique scripts"
                    value={fmtNumber(uniqueCount)}
                    sub={`Across ${fmtNumber(totalHits)} observation${totalHits === 1 ? "" : "s"}`}
                  />
                  <StatusTile
                    Icon={Globe}
                    label="Third party"
                    value={fmtNumber(counts.third_party)}
                    sub="External domains — PCI DSS 6.4.3 focus"
                  />
                  <StatusTile
                    Icon={Home}
                    label="First party"
                    value={fmtNumber(counts.first_party)}
                    sub="Loaded from your own hosts"
                  />
                  <StatusTile
                    Icon={Code2}
                    label="Inline / extension"
                    value={fmtNumber((counts.inline || 0) + (counts.extension || 0))}
                    sub={`${fmtNumber(counts.inline)} inline, ${fmtNumber(counts.extension)} extension`}
                  />
                </Stack>
                {/* PCI DSS 6.4.3 authorization progress bar */}
                <Box sx={{ mt: 2 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: FG2, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      Authorization progress
                    </Typography>
                    <Stack direction="row" spacing={0.75}>
                      <Chip size="small" label={`${fmtNumber(statusCounts.authorized)} authorized`} sx={{ fontSize: 11, height: 20, background: SUCCESS_BG, color: SUCCESS_FG, border: `1px solid ${SUCCESS_BORDER}`, fontWeight: 600 }} />
                      <Chip size="small" label={`${fmtNumber(statusCounts.unauthorized)} unauthorized`} sx={{ fontSize: 11, height: 20, background: DANGER_BG, color: DANGER_FG, border: `1px solid ${DANGER_BORDER}`, fontWeight: 600 }} />
                      <Chip size="small" label={`${fmtNumber(statusCounts.pending)} pending`} sx={{ fontSize: 11, height: 20, background: AMBER_BG, color: AMBER_FG, border: `1px solid ${AMBER_BORDER}`, fontWeight: 600 }} />
                    </Stack>
                  </Stack>
                  <Box sx={{ display: "flex", height: 8, borderRadius: "4px", overflow: "hidden", background: NEUTRAL_BG }}>
                    {uniqueCount > 0 && (
                      <>
                        <Box sx={{ width: `${Math.round(((statusCounts.authorized || 0) * 100) / uniqueCount)}%`, background: SUCCESS_FG }} />
                        <Box sx={{ width: `${Math.round(((statusCounts.unauthorized || 0) * 100) / uniqueCount)}%`, background: DANGER_FG }} />
                        <Box sx={{ width: `${Math.round(((statusCounts.pending || 0) * 100) / uniqueCount)}%`, background: AMBER_FG }} />
                      </>
                    )}
                  </Box>
                </Box>
              </SectionCard>

              {/* Phase 3.3 — drift alert. PCI DSS 11.6.1 wants an
                  operator visible signal when an authorized script's
                  content has changed since the last approved
                  reference. */}
              {driftedCount > 0 && (
                <Box
                  sx={{
                    padding: "14px 18px",
                    borderRadius: "12px",
                    background: DANGER_BG,
                    border: `1px solid ${DANGER_BORDER}`,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <ErrorOutlineOutlinedIcon sx={{ color: DANGER_FG, fontSize: 22 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: DANGER_FG }}>
                      {driftedCount} authorized script{driftedCount === 1 ? "" : "s"} changed since last approved
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: DANGER_FG }}>
                      PCI DSS 11.6.1 tamper-detection: review the drifted rows below and either acknowledge the new content (rotate the reference hash) or investigate.
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setStatusFilter("all") || setFilter("all")}
                    sx={{ borderColor: DANGER_FG, color: DANGER_FG, textTransform: "none", fontWeight: 600 }}
                  >
                    Show drifted
                  </Button>
                </Box>
              )}

              {/* Phase 4c — Subresource Integrity coverage banner.
                  Shows the current site-level SRI mode + the count of
                  authorized rows that will actually get integrity=
                  injected at the edge (authorized + has a recorded
                  content_sha256). A green banner when enforcing, a
                  neutral one when off (offering the operator a hint
                  that they can turn it on). */}
              {sriMode === "enforce" ? (
                <Box
                  sx={{
                    padding: "12px 16px",
                    borderRadius: "12px",
                    background: SUCCESS_BG,
                    border: `1px solid ${SUCCESS_BORDER}`,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <LockOutlinedIcon sx={{ color: SUCCESS_FG, fontSize: 20 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: SUCCESS_FG }}>
                      SRI enforcement is ON — {sriEligibleCount} of {authorizedCount} authorized scripts will be integrity-verified
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: SUCCESS_FG }}>
                      {authorizedCount - sriEligibleCount > 0
                        ? `${authorizedCount - sriEligibleCount} authorized script${authorizedCount - sriEligibleCount === 1 ? " has" : "s have"} no reference hash yet. Run "Check integrity" or wait for the daily watcher to seed it — until then, that script passes through un-verified.`
                        : `Every authorized script has a recorded SHA-256. Browsers will refuse to run anything whose fetched bytes drift from the reference.`}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    padding: "12px 16px",
                    borderRadius: "12px",
                    background: NEUTRAL_BG,
                    border: `1px solid ${BORDER}`,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <LockOutlinedIcon sx={{ color: FG2, fontSize: 20 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: FG1 }}>
                      SRI enforcement is OFF for this site
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: FG2 }}>
                      {sriEligibleCount > 0
                        ? `${sriEligibleCount} authorized script${sriEligibleCount === 1 ? " is" : "s are"} ready for integrity enforcement. Enable it on the CSP config page to have browsers verify script content in addition to the daily drift check.`
                        : `Authorize scripts and run "Check integrity" to seed reference hashes before enabling SRI enforcement on the CSP config page.`}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Filter + search + table */}
              <SectionCard
                icon={<SearchOutlinedIcon />}
                title="Detail"
                desc="Filter by classification or authorization status, search by URL / host, click the gavel icon to review a script. Sort is fixed to hit-count desc."
              >
                {/* Two chip rows: classification + status. Both filters
                    are AND-composed. */}
                <AsecEventsFilterBar
                  sx={{ mb: 2 }}
                  search={query}
                  onSearchChange={(v) => {
                    setQuery(v);
                    setPage(0);
                  }}
                  searchPlaceholder="Search URL or host…"
                >
                  <Select
                    value={filter}
                    onChange={(e) => {
                      setFilter(e.target.value);
                      setPage(0);
                    }}
                    sx={{ ...filterSelectSx, minWidth: 170 }}
                  >
                    {filterOptions.map((f) => (
                      <MenuItem key={f.key} value={f.key}>
                        {`${f.label} (${fmtNumber(f.count)})`}
                      </MenuItem>
                    ))}
                  </Select>
                  <Select
                    value={windowHours}
                    onChange={(e) => setWindowHours(Number(e.target.value))}
                    sx={{ ...filterSelectSx, minWidth: 120 }}
                  >
                    {WINDOW_OPTIONS.map((w) => (
                      <MenuItem key={w.hours} value={w.hours}>
                        {w.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <Select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPage(0);
                    }}
                    sx={{ ...filterSelectSx, minWidth: 220 }}
                  >
                    {statusFilterOptions.map((f) => (
                      <MenuItem key={f.key} value={f.key}>
                        {`${f.label} (${fmtNumber(f.count)})`}
                      </MenuItem>
                    ))}
                  </Select>
                </AsecEventsFilterBar>

                {filtered.length === 0 && (
                  <Box sx={{ textAlign: "center", padding: "36px 20px" }}>
                    <Typography sx={{ fontSize: 13.5, color: FG2, mb: 0.5 }}>
                      No scripts match the current filters.
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: FG3 }}>
                      {uniqueCount === 0
                        ? "Flip a site into discovery mode to start collecting script observations."
                        : "Clear the search or pick a different classification filter."}
                    </Typography>
                  </Box>
                )}

                {filtered.length > 0 && (
                  <>
                    {/* Bulk-action bar — only rendered when the operator
                        has selected at least one row. Sticks above the
                        table so the actions stay in view while
                        scrolling long inventories. */}
                    {selectedUrls.size > 0 && (
                      <Box sx={{
                        display: "flex", alignItems: "center", gap: 2,
                        px: "18px", py: "10px", mb: "10px",
                        background: "#EDF3FE", border: `1px solid #C7DAFB`,
                        borderRadius: "10px",
                      }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0244BE", flex: 1 }}>
                          {selectedUrls.size} script{selectedUrls.size === 1 ? "" : "s"} selected
                        </Typography>
                        <Button
                          onClick={() => handleBulkAuthorize("authorized")}
                          disabled={bulkBusy}
                          size="small"
                          variant="contained"
                          sx={{ background: "#6371F7", textTransform: "none", fontWeight: 500, fontSize: 12.5, "&:hover": { background: "#454FAD" } }}
                        >
                          Authorize all
                        </Button>
                        <Button
                          onClick={() => handleBulkAuthorize("unauthorized")}
                          disabled={bulkBusy}
                          size="small"
                          variant="outlined"
                          sx={{ borderColor: "#F7C5CC", color: "#A11228", textTransform: "none", fontWeight: 500, fontSize: 12.5, "&:hover": { borderColor: "#FB3748", background: "rgba(251,55,72,0.06)" } }}
                        >
                          Mark unauthorized
                        </Button>
                        <Button
                          onClick={() => setSelectedUrls(new Set())}
                          disabled={bulkBusy}
                          size="small"
                          variant="text"
                          sx={{ color: FG2, textTransform: "none", fontSize: 12.5 }}
                        >
                          Clear
                        </Button>
                      </Box>
                    )}
                    <Box sx={{ overflowX: "auto" }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell padding="checkbox">
                              <Checkbox
                                size="small"
                                sx={{ color: "#B0B0B0", "&.Mui-checked": { color: "#6371F7" }, "&.MuiCheckbox-indeterminate": { color: "#6371F7" } }}
                                indeterminate={paged.some((s) => selectedUrls.has(s.url)) && !paged.every((s) => selectedUrls.has(s.url))}
                                checked={paged.length > 0 && paged.every((s) => selectedUrls.has(s.url))}
                                onChange={(e) => {
                                  setSelectedUrls((prev) => {
                                    const next = new Set(prev);
                                    if (e.target.checked) paged.forEach((s) => next.add(s.url));
                                    else paged.forEach((s) => next.delete(s.url));
                                    return next;
                                  });
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ fontSize: 11, color: FG3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>URL</TableCell>
                            <TableCell sx={{ fontSize: 11, color: FG3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>Classification</TableCell>
                            <TableCell sx={{ fontSize: 11, color: FG3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>Status</TableCell>
                            <TableCell sx={{ fontSize: 11, color: FG3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>Integrity</TableCell>
                            <TableCell sx={{ fontSize: 11, color: FG3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>SRI</TableCell>
                            <TableCell sx={{ fontSize: 11, color: FG3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>Host</TableCell>
                            <TableCell sx={{ fontSize: 11, color: FG3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>First seen</TableCell>
                            <TableCell sx={{ fontSize: 11, color: FG3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>Last seen</TableCell>
                            <TableCell align="right" sx={{ fontSize: 11, color: FG3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>Hits</TableCell>
                            <TableCell />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {paged.map((s, i) => {
                            const meta = CLASSIFICATION_META[s.classification] || CLASSIFICATION_META.unknown;
                            const isSelected = selectedUrls.has(s.url);
                            const isHighlighted = highlightUrl && s.url === highlightUrl;
                            return (
                              <TableRow
                                key={`${s.url}-${i}`}
                                hover
                                selected={isSelected}
                                sx={isHighlighted ? { background: "rgba(99,113,247,0.08)" } : undefined}
                              >
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    size="small"
                                    sx={{ color: "#B0B0B0", "&.Mui-checked": { color: "#6371F7" } }}
                                    checked={isSelected}
                                    onChange={(e) => {
                                      setSelectedUrls((prev) => {
                                        const next = new Set(prev);
                                        if (e.target.checked) next.add(s.url);
                                        else next.delete(s.url);
                                        return next;
                                      });
                                    }}
                                  />
                                </TableCell>
                                <TableCell sx={{ fontSize: 12.5, color: FG1, fontFamily: "ui-monospace, SFMono-Regular, monospace", maxWidth: 420, wordBreak: "break-all" }}>
                                  <Box>{shortenUri(s.url, 72)}</Box>
                                  {(() => {
                                    const si = sharedSignatures[s.url];
                                    // Only render the chip when we've seen the URL at ≥3 tenants —
                                    // below that the signal is too weak to guide a decision.
                                    if (!si || si.tenants < 3) return null;
                                    return (
                                      <Tooltip title={`This URL has been blocked at ${si.tenants} Layerd tenants in the last 30d — likely a widely-embedded third party rather than a targeted attack.`}>
                                        <Chip
                                          size="small"
                                          label={`seen at ${si.tenants} sites`}
                                          sx={{
                                            mt: "6px",
                                            fontFamily: "system-ui, sans-serif",
                                            fontSize: 10.5,
                                            height: 18,
                                            fontWeight: 600,
                                            background: "#EDF3FE",
                                            color: "#0244BE",
                                            border: "1px solid #C7DAFB",
                                          }}
                                        />
                                      </Tooltip>
                                    );
                                  })()}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={meta.label}
                                    size="small"
                                    sx={{
                                      fontSize: 11,
                                      height: 20,
                                      fontWeight: 600,
                                      background: meta.bg,
                                      color: meta.fg,
                                      border: `1px solid ${meta.br}`,
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  {(() => {
                                    const sm = STATUS_META[s.status || "pending"] || STATUS_META.pending;
                                    return (
                                      <Chip
                                        label={sm.label}
                                        size="small"
                                        sx={{
                                          fontSize: 11,
                                          height: 20,
                                          fontWeight: 600,
                                          background: sm.bg,
                                          color: sm.fg,
                                          border: `1px solid ${sm.br}`,
                                        }}
                                      />
                                    );
                                  })()}
                                </TableCell>
                                <TableCell>
                                  {(() => {
                                    const key = s.last_check_status || "unchecked";
                                    const im = INTEGRITY_META[key] || INTEGRITY_META.unchecked;
                                    const Icon = im.Icon;
                                    const title = s.last_check_error
                                      ? `${im.label}: ${s.last_check_error}`
                                      : s.last_checked_at
                                      ? `${im.label} · last checked ${fmtWhen(s.last_checked_at)}`
                                      : im.label;
                                    return (
                                      <Tooltip title={title}>
                                        <Chip
                                          icon={<Icon fontSize="small" sx={{ color: im.fg + " !important" }} />}
                                          label={im.label}
                                          size="small"
                                          sx={{
                                            fontSize: 11,
                                            height: 22,
                                            fontWeight: 600,
                                            background: im.bg,
                                            color: im.fg,
                                            border: `1px solid ${im.br}`,
                                            "& .MuiChip-icon": { color: im.fg, ml: "6px", mr: "-4px" },
                                          }}
                                        />
                                      </Tooltip>
                                    );
                                  })()}
                                </TableCell>
                                <TableCell>
                                  {(() => {
                                    const rowState = sriRowState(s);
                                    const rm = SRI_ROW_META[rowState];
                                    return (
                                      <Tooltip title={rm.tip}>
                                        <Chip
                                          label={rm.label}
                                          size="small"
                                          sx={{
                                            fontSize: 11,
                                            height: 20,
                                            fontWeight: 600,
                                            background: rm.bg,
                                            color: rm.fg,
                                            border: `1px solid ${rm.br}`,
                                          }}
                                        />
                                      </Tooltip>
                                    );
                                  })()}
                                </TableCell>
                                <TableCell sx={{ fontSize: 12.5, color: FG2, fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>
                                  {s.host || "—"}
                                </TableCell>
                                <TableCell sx={{ fontSize: 12, color: FG2, whiteSpace: "nowrap" }}>
                                  {fmtWhen(s.first_seen_ts)}
                                </TableCell>
                                <TableCell sx={{ fontSize: 12, color: FG2, whiteSpace: "nowrap" }}>
                                  {fmtWhen(s.last_seen_ts)}
                                </TableCell>
                                <TableCell align="right" sx={{ fontSize: 13, color: FG1, fontWeight: 600 }}>
                                  {fmtNumber(s.hit_count)}
                                </TableCell>
                                <TableCell align="right" sx={{ width: 96, whiteSpace: "nowrap" }}>
                                  <Tooltip title="Review authorization">
                                    <IconButton size="small" onClick={() => setReviewRow(s)}>
                                      <GavelOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Copy URL">
                                    <IconButton size="small" onClick={() => handleCopyUrl(s.url)}>
                                      <ContentCopyOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </Box>
                    <TablePagination
                      component="div"
                      count={scriptsTotal}
                      page={page}
                      onPageChange={(_, p) => setPage(p)}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={(e) => {
                        setRowsPerPage(Number(e.target.value));
                        setPage(0);
                      }}
                      rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                  </>
                )}
              </SectionCard>
              </Stack>
            )}
          </Stack>

          <ReviewDialog
            row={reviewRow}
            onClose={() => setReviewRow(null)}
            onSave={handleSaveReview}
            onAcknowledgeDrift={handleAcknowledgeDrift}
          />
        </>
      )}
    </SiteScopeGate>
  );
}

// Review-authorization dialog. The parent owns the "which row"
// state; this component owns the draft (status + justification) so
// the operator can cancel without side effects on the underlying
// inventory.
function ReviewDialog({ row, onClose, onSave, onAcknowledgeDrift }) {
  const [status, setStatus] = useState(row?.status || "pending");
  const [justification, setJustification] = useState(row?.justification || "");
  const [saving, setSaving] = useState(false);
  const [acking, setAcking] = useState(false);

  useEffect(() => {
    setStatus(row?.status || "pending");
    setJustification(row?.justification || "");
  }, [row]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await onSave({ status, justification });
    } finally {
      setSaving(false);
    }
  }, [onSave, status, justification]);

  return (
    <Dialog
      open={!!row}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "20px", overflow: "hidden", background: "#fff" } }}
    >
      <DialogTitle
        sx={{
          padding: "18px 24px",
          background: "#fff",
          borderBottom: `1px solid ${BORDER}`,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "10px",
            background: INDIGO_BG,
            color: INDIGO,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "& svg": { width: 20, height: 20 },
          }}
        >
          <GavelOutlinedIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 16, color: FG1 }}>
            Review script authorization
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: FG2, mt: 0.25 }}>
            PCI DSS 4.0.1 6.4.3 requires a status + justification for every payment-page script.
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseOutlinedIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ padding: "20px 24px 18px", background: "#fff" }}>
        {row && (
          <Stack spacing={2}>
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: FG3, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Script URL
              </Typography>
              <Typography sx={{ fontSize: 13, color: FG1, fontFamily: "ui-monospace, SFMono-Regular, monospace", mt: 0.5, wordBreak: "break-all" }}>
                {row.url}
              </Typography>
            </Box>

            <Stack direction="row" spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: FG3, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Classification
                </Typography>
                <Typography sx={{ fontSize: 13, color: FG1, mt: 0.5 }}>
                  {(CLASSIFICATION_META[row.classification] || CLASSIFICATION_META.unknown).label}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: FG3, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Host
                </Typography>
                <Typography sx={{ fontSize: 13, color: FG1, mt: 0.5, fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>
                  {row.host || "—"}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: FG3, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Hits
                </Typography>
                <Typography sx={{ fontSize: 13, color: FG1, mt: 0.5, fontWeight: 600 }}>
                  {fmtNumber(row.hit_count)}
                </Typography>
              </Box>
            </Stack>

            {row.authorized_by && (
              <Box sx={{ padding: "10px 14px", background: NEUTRAL_BG, borderRadius: "10px", border: `1px solid ${BORDER}` }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: FG3, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Previous decision
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: FG2, mt: 0.5 }}>
                  <b>{row.authorized_by}</b> — {fmtWhen(row.authorized_at)}
                </Typography>
              </Box>
            )}

            {/* Phase 3.3 — integrity summary. Rendered for every row
                so the operator sees the reference hash + when it was
                last checked; drift shows a red panel + Acknowledge
                CTA. */}
            {(row.last_check_status && row.last_check_status !== "unchecked") && (
              <Box
                sx={{
                  padding: "10px 14px",
                  background: row.last_check_status === "changed" ? DANGER_BG : NEUTRAL_BG,
                  borderRadius: "10px",
                  border: `1px solid ${row.last_check_status === "changed" ? DANGER_BORDER : BORDER}`,
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: row.last_check_status === "changed" ? DANGER_FG : FG3, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      Integrity check
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: FG2, mt: 0.5 }}>
                      Last checked <b>{fmtWhen(row.last_checked_at)}</b> — status <b>{(INTEGRITY_META[row.last_check_status] || INTEGRITY_META.unchecked).label}</b>
                    </Typography>
                    {row.content_sha256 && (
                      <Typography sx={{ fontSize: 11, color: FG3, mt: 0.5, fontFamily: "ui-monospace, SFMono-Regular, monospace", wordBreak: "break-all" }}>
                        Reference SHA-256: {row.content_sha256}
                      </Typography>
                    )}
                    {row.last_check_error && (
                      <Typography sx={{ fontSize: 11.5, color: DANGER_FG, mt: 0.5 }}>
                        {row.last_check_error}
                      </Typography>
                    )}
                    {row.first_mismatch_at && (
                      <Typography sx={{ fontSize: 11.5, color: DANGER_FG, mt: 0.5 }}>
                        First mismatch seen: {fmtWhen(row.first_mismatch_at)}
                      </Typography>
                    )}
                  </Box>
                  {row.last_check_status === "changed" && (
                    <Button
                      onClick={async () => {
                        setAcking(true);
                        try { await onAcknowledgeDrift(row); } finally { setAcking(false); }
                      }}
                      disabled={acking}
                      size="small"
                      variant="contained"
                      sx={{
                        background: DANGER_FG,
                        color: "#fff",
                        textTransform: "none",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        "&:hover": { background: "#8a0d1f" },
                      }}
                    >
                      {acking ? "Acknowledging…" : "Acknowledge new hash"}
                    </Button>
                  )}
                </Stack>
              </Box>
            )}

            <Divider />

            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: FG2, textTransform: "uppercase", letterSpacing: "0.05em", mb: 1 }}>
                Decision
              </Typography>
              <RadioGroup value={status} onChange={(e) => setStatus(e.target.value)}>
                {["authorized", "unauthorized", "pending"].map((k) => {
                  const m = STATUS_META[k];
                  return (
                    <FormControlLabel
                      key={k}
                      value={k}
                      control={<Radio size="small" />}
                      label={
                        <Chip
                          label={m.label}
                          size="small"
                          sx={{
                            fontSize: 11,
                            height: 20,
                            fontWeight: 600,
                            background: m.bg,
                            color: m.fg,
                            border: `1px solid ${m.br}`,
                          }}
                        />
                      }
                    />
                  );
                })}
              </RadioGroup>
            </Box>

            <TextField
              multiline
              minRows={3}
              maxRows={8}
              fullWidth
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              label="Justification"
              placeholder="Why is this script (un)authorized on payment pages? What was reviewed and by whom?"
              inputProps={{ maxLength: 4096 }}
              helperText={`${justification.length} / 4096 characters`}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  background: "#fff",
                  "& fieldset": { borderColor: BORDER },
                  "&:hover fieldset": { borderColor: INDIGO },
                  "&.Mui-focused fieldset": { borderColor: INDIGO },
                },
                "& .MuiInputBase-input::placeholder": {
                  color: FG3,
                  opacity: 1,
                },
              }}
            />
          </Stack>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          padding: "14px 24px",
          borderTop: `1px solid ${BORDER}`,
          background: "#fff",
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.5,
        }}
      >
        <Button onClick={onClose} sx={{ ...cancelButtonSx, width: 140 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          variant="contained"
          startIcon={<PolicyOutlinedIcon />}
          sx={{
            ...primaryButtonSx,
            width: 220,
            height: 44,
            fontSize: 15,
            fontWeight: 600,
          }}
        >
          {saving ? "Saving…" : "Save decision"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ScriptInventory;
